import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { Admin, DistrictShort, SchoolShort } from 'src/app/shared/graphql';
import { AuthService } from 'src/app/shared/services';
import { StorageV3Service } from './storage-v3.service';
import { TelemetrySources } from '../interfaces/telemetry/telemetry-sources';


@Injectable({
  providedIn: 'root'
})
export class AdminV3Service {
  private _districtId = '';
  private _schoolId = '';
  private _classId = '';
  private _currentAdmin!: Admin | null;
  private _currentAdmin$ = new BehaviorSubject<Admin | null>(null);
  public currentAdmin$ = this._currentAdmin$.asObservable();


  constructor(private authService: AuthService, private storageService: StorageV3Service ){
  }

   getDistrictId(): string {
    if (this._districtId) {
      return this._districtId; // Devuelve el valor local si existe
    }
    // Si no hay valor local, lo busca en AuthService
    const lastDistrictId = this.authService.getLastDistrictId();
    if (lastDistrictId) {
      this._districtId = lastDistrictId;
    }
    return lastDistrictId || '';
  }

   setDistrictId(value: string): void {
    this._districtId = value;
    this.authService.setLastDistrict(value);
  }

  resetCurrentAdmin(): void {
    this._currentAdmin = null;
  }

  getSchoolId(): string {
    if (this._schoolId) return this._schoolId; 

    const lastSchoolId = this.storageService.getLastSchoolId();
    if (lastSchoolId ){
      const district = this.getSelectedAdminDistrict();
      const school = district?.schools?.find((school) => school?.schoolId === lastSchoolId);
      if(school){
         this._schoolId = lastSchoolId;
          return this._schoolId;
      } else {
        return this.getDefaultSchool()?.schoolId as string;
      }   
    } else {
      return this.getDefaultSchool()?.schoolId as string;
    }   
  }

  setSchoolId(value: string): void {
    this._schoolId = value;
    this.storageService.setLastSchoolId(value);
  }

  getDefaultSchool(): SchoolShort | null {
    if (!this._currentAdmin?.districts) return null;
    
    const district = this._currentAdmin.districts.find(d => 
      d?.districtId === this.getDistrictId()
    );
    
    return district?.schools?.[0] || null;
  }

  getClassId(): string {
    return this._classId;
  }

  setClassId(value: string): void {
    this._classId = value;
  }

  getCurrentAdmin(): Admin  | null {
    if (this._currentAdmin) return this._currentAdmin;
    return this.storageService.getCurrent();
  }

  setCurrentAdmin(value: Admin, noPropagate?: boolean): void {
    this._currentAdmin = value;
    if(!noPropagate){
      this._currentAdmin$.next(value);
    }   
    this.storageService.setCurrent(value);
  }
 
  getDistricts(): Array<DistrictShort> | [] {
    if (this._currentAdmin?.districts) return this._currentAdmin.districts as Array<DistrictShort>;
    return this.storageService.getDistricts() || [];
  }

  setDistricts(districts: DistrictShort[]): void {
    try {
        this.authService.setDistricts(districts);
        
        this._currentAdmin = {
                ...this._currentAdmin, // Copia todas las propiedades existentes
                districts,             // Sobrescribe solo `districts`
         };
        
        this.storageService.updateDistrictsInCurrent(districts);
    } catch (error) {
        console.error('Error in setDistricts:', error);
        throw error; 
    }
}

  replaceRouteParams(routeTemplate: string): string {
    let result = routeTemplate;
    
    // Only process replacements if the template contains the parameter
    if (result.includes(':districtId')) {
      result = result.replace(':districtId', this.getDistrictId());
    }
    
    if (result.includes(':schoolId')) {
      const schoolId = this.getSchoolId() || this.getDefaultSchool()?.schoolId || '';
      result = result.replace(':schoolId', schoolId);
    }
    
    if (result.includes(':classId')) {
      result = result.replace(':classId', this.getClassId());
    }
    
    return result;
  }

  isNotLoggedIn(): boolean {
    return this.authService.isLoggedIn !== true;
  }

  getAdminSchoolById(schoolId?: string): SchoolShort | undefined {
    const district = this.getSelectedAdminDistrict();
    const id =  schoolId || this.getSchoolId();
    const school = district?.schools?.find(x=>x?.schoolId === id);
    return school??undefined;
  }

  getSelectedAdminDistrict(): DistrictShort | undefined {
    const districtId = this.getDistrictId();
    return this.getDistricts().find(x=>x.districtId === districtId);
  }

  addSchoolToDistrict(districtId: string, school: SchoolShort): void {
    const currentAdmin = this.getCurrentAdmin();
    if (!currentAdmin?.districts) return;
  
    // Clonar y filtrar distritos válidos
    const validDistricts = currentAdmin.districts
      .filter((d): d is DistrictShort => !!d?.districtId)
      .map(d => ({ ...d, schools: d.schools ? [...d.schools] : [] }));
  
    // Actualizar admin
    const updatedAdmin: Admin = {
      ...currentAdmin,
      districts: validDistricts.map(d => 
        d.districtId === districtId
          ? { ...d, schools: [...d.schools, school] }
          : d
      )
    };
    this.setCurrentAdmin(updatedAdmin, true);
    this.storageService.addSchoolToDistrict(districtId, school);
  }

  removeSchoolFromDistrict(districtId: string, schoolId: string): void {
    const currentAdmin = this.getCurrentAdmin();
    if (!currentAdmin?.districts) return;
  
    // Clonar y filtrar distritos válidos
    const validDistricts = currentAdmin.districts
      .filter((d): d is DistrictShort => !!d?.districtId)
      .map(d => ({ ...d, schools: d.schools ? [...d.schools] : [] }));
  
    // Actualizar admin
    const updatedAdmin: Admin = {
      ...currentAdmin,
      districts: validDistricts.map(d => {
        if (d.districtId !== districtId) return d;
        return {
          ...d,
          schools: schoolId 
            ? d.schools.filter(s => s?.schoolId !== schoolId)
            : []
        };
      })
    };  
    this.setCurrentAdmin(updatedAdmin, true);
    this.storageService.removeSchoolFromDistrict(districtId, schoolId);
  }

  removeCurrent(): void {
      this._currentAdmin = null;
      this.storageService.removeCurrent();

  }
  
  getSchoolsByDistrictId(districtId: string): Array<SchoolShort> {
    const districts = this.getDistricts();
    const d = districts.find((dist) => dist?.districtId === districtId);
    return d?.schools ? d?.schools as Array<SchoolShort>: [];
  }

  isTeacher(): boolean {
    const currentDistrict = this.getSelectedAdminDistrict();
    const currentSchoolId = this.getSchoolId();
    
    if (!currentDistrict) {
        return false;
    }

    // If district has a role defined, it's not a teacher
    if (currentDistrict.role && currentDistrict.role !== '') {
        return false;
    }

    // If there's a school selected
    if (currentSchoolId) {
        const currentSchool = this.getAdminSchoolById(currentSchoolId);
        // Teacher condition: school exists and has no role or empty role
        return currentSchool ? (!currentSchool.role || currentSchool.role === '') : false;
    }
    return false;
  }

  getTelemetrySources(): TelemetrySources {
     return {
      classId: this.getClassId(),
      schoolId: this.getSchoolId(),
      districtId: this.getDistrictId()
     }
  }

  findDistrictIdBySchoolId(schoolId: string): string | null {
      const districts = this.getDistricts();
      if (!districts || !schoolId) {
        return null;
      }

      const district = districts.find(d => 
        d?.schools?.some(school => school?.schoolId === schoolId)
      );

      return district?.districtId || null;
   }
}
